import { z } from 'zod';
import { AccountIdSchema, ProfileIdSchema, PlayerIdSchema } from './common.js';

/**
 * Auth multi-device + multi-profile (PLAN_REVIEW2 korekta 1).
 *
 * Model:
 *   account (rodzic/PIN) → wiele profiles (pieski/smoki)
 *   register zakłada konto + 1 profil
 *   login weryfikuje parent credential → token konta
 *   profile-select wybiera aktywny profil (UI auto-wybiera gdy 1)
 *   recovery odzyskuje dostęp do konta
 *
 * Token zawsze w nagłówku Authorization — NIGDY w URL.
 */

/** PIN opiekuna: 4–8 cyfr (przyjazne dziecku, bez e-maila). */
export const ParentCredentialSchema = z
  .string()
  .regex(/^\d{4,8}$/, 'PIN opiekuna: 4–8 cyfr');

export const CharacterKindSchema = z.enum(['dog', 'dragon']);
export type CharacterKind = z.infer<typeof CharacterKindSchema>;

export const ProfileSummarySchema = z.object({
  profileId: ProfileIdSchema,
  playerId: PlayerIdSchema, // alias: playerId === profileId (kompatybilność WP1–WP17)
  dogName: z.string(),
  breed: z.string(),
  color: z.string(),
  characterKind: CharacterKindSchema,
  displayName: z.string().optional(),
});
export type ProfileSummary = z.infer<typeof ProfileSummarySchema>;

// ── POST /api/auth/register ──────────────────────────────────────────
export const RegisterRequestSchema = z.object({
  parentCredential: ParentCredentialSchema,
  /** Opcjonalny kod odzyskania (wygenerowany przez serwer jeśli brak). */
  recoveryHint: z.string().max(64).optional(),
  dogName: z.string().min(1).max(24),
  breed: z.string().min(1),
  color: z.string().min(1),
  characterKind: CharacterKindSchema.default('dog'),
  displayName: z.string().min(1).max(24).optional(),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

export const RegisterResponseSchema = z.object({
  accountId: AccountIdSchema,
  profileId: ProfileIdSchema,
  playerId: PlayerIdSchema,
  token: z.string().min(32),
  /** Zwracany RAZ — klient pokazuje rodzicowi; serwer trzyma hash. */
  recoveryCode: z.string().min(8),
  profiles: z.array(ProfileSummarySchema).min(1),
});
export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

// ── POST /api/auth/login ─────────────────────────────────────────────
export const LoginRequestSchema = z.object({
  /** Id konta (zapamiętane lokalnie) LUB recoveryCode. */
  accountId: AccountIdSchema.optional(),
  parentCredential: ParentCredentialSchema,
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const LoginResponseSchema = z.object({
  accountId: AccountIdSchema,
  token: z.string().min(32),
  profiles: z.array(ProfileSummarySchema),
  /** Gdy jeden profil — serwer może od razu go „wybrać"; klient i tak wywołuje profile-select. */
  autoSelectedProfileId: ProfileIdSchema.optional(),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

// ── POST /api/auth/profile-select ────────────────────────────────────
export const ProfileSelectRequestSchema = z.object({
  profileId: ProfileIdSchema,
});
export type ProfileSelectRequest = z.infer<typeof ProfileSelectRequestSchema>;

export const ProfileSelectResponseSchema = z.object({
  profileId: ProfileIdSchema,
  playerId: PlayerIdSchema,
  token: z.string().min(32),
  profile: ProfileSummarySchema,
});
export type ProfileSelectResponse = z.infer<typeof ProfileSelectResponseSchema>;

// ── POST /api/auth/recovery ──────────────────────────────────────────
export const RecoveryRequestSchema = z.object({
  recoveryCode: z.string().min(8).max(64),
  newParentCredential: ParentCredentialSchema,
});
export type RecoveryRequest = z.infer<typeof RecoveryRequestSchema>;

export const RecoveryResponseSchema = z.object({
  accountId: AccountIdSchema,
  token: z.string().min(32),
  profiles: z.array(ProfileSummarySchema),
});
export type RecoveryResponse = z.infer<typeof RecoveryResponseSchema>;

// ── POST /api/auth/verify-pin ────────────────────────────────────────
/** Bramka rodzica: weryfikacja PIN bez rotacji sesji (ustawienia / usuwanie). */
export const VerifyPinRequestSchema = z.object({
  parentCredential: ParentCredentialSchema,
});
export type VerifyPinRequest = z.infer<typeof VerifyPinRequestSchema>;

export const VerifyPinResponseSchema = z.object({
  ok: z.literal(true),
});
export type VerifyPinResponse = z.infer<typeof VerifyPinResponseSchema>;

// ── POST /api/auth/reset ─────────────────────────────────────────────
/** Usunięcie konta — wymaga PIN opiekuna (nie losowej matmy). */
export const ResetRequestSchema = z.object({
  confirm: z.literal(true),
  parentCredential: ParentCredentialSchema,
});
export type ResetRequest = z.infer<typeof ResetRequestSchema>;

export const ResetResponseSchema = z.object({
  ok: z.literal(true),
});
export type ResetResponse = z.infer<typeof ResetResponseSchema>;

// ── GET /api/me ──────────────────────────────────────────────────────
export const MeResponseSchema = z.object({
  accountId: AccountIdSchema,
  profileId: ProfileIdSchema,
  playerId: PlayerIdSchema,
  dogName: z.string(),
  breed: z.string(),
  color: z.string(),
  characterKind: CharacterKindSchema,
  balanceZl: z.number().int().nonnegative(),
  onboardingCompleted: z.boolean(),
  startingTier: z.enum(['bogaty', 'normalny', 'biedny']).nullable(),
  gardenUnlocked: z.boolean(),
  settings: z.object({
    musicOn: z.boolean(),
    sfxOn: z.boolean(),
  }),
  profileVersion: z.number().int().nonnegative(),
  // token NIGDY nie jest w /api/me
});
export type MeResponse = z.infer<typeof MeResponseSchema>;
